import { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { Interaction, SubTask, SubTaskGroup, SubTaskPrompt, SubTaskRole, SubTaskStudent } from "../../interfaces/Task";
import SubTaskGroupItem from "../SubTask/SubTaskGroupItem";
import SubTaskRoleItem from "../SubTask/SubTaskRoleItem";
import SubTaskPromptItem from "../SubTask/SubTaskPromptItem";
import SubTaskPromptEdit from "../SubTask/SubTaskPromptEdit";
import SubTaskRoleItemAssignment from "../SubTask/SubTaskRoleItemAssignment";
import Help from "../../elements/HelpContent";
import { Media } from "../../interfaces/Media";
import { useTranslation } from "react-i18next";
import { Overlay } from "../../elements/Overlay";

/**
 * EditInteraction — editor del sub-document `interaction` d'una pre/post-task
 * (estructura tipus Main task: grups/rols/prompts).
 *
 * Component SEPARAT d'EditSubTask a propòsit (decisió de disseny §4.1): el
 * Main task i la Interaction poden evolucionar independentment. Reutilitza els
 * widgets fulla (SubTaskGroupItem, SubTaskRoleItem, SubTaskPromptEdit...) perquè
 * operen sobre la forma universal grups/rols/prompts; la separació és al nivell
 * d'orquestració, que és on és probable la divergència.
 *
 * Totalment controlat: no fa cap crida API. Cada canvi crida saveCallback amb
 * la nova Interaction; la persistència la fa el pare (UnifiedTaskEditor).
 */
export interface EditInteractionProps {
    taskId: string;
    task_language?: string;
    interaction: Interaction;
    saveCallback: CallableFunction;
    closeCallback: CallableFunction;
    title?: string;
    // Quan és true, el dissenyador es renderitza sense el seu propi Overlay
    // (el pare l'incrusta dins del seu modal). Vegeu UnifiedTaskEditor.
    embedded?: boolean;
    // En mode embedded, notifica el pare del títol de la sub-vista activa
    // (prompt/rol) perquè actualitzi la capçalera del modal; null = dissenyador.
    onTitle?: (title: string | null) => void;
}

export default function EditInteraction(props: EditInteractionProps) {
    const { t } = useTranslation();

    const [showEditPrompt, setShowEditPrompt] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState<SubTaskPrompt | null>(null);
    const [selectedPromptPosition, setSelectedPromptPosition] = useState(0);
    const [showEditRoleAssignment, setShowEditRoleAssignment] = useState(false);
    const [selectedRole, setSelectedRole] = useState<SubTaskRole | null>(null);
    const [selectedRolePosition, setSelectedRolePosition] = useState(0);

    // Adaptador per als leaves que declaren un prop `subTask` (només en llegeixen
    // groups/roles/prompts, que la Interaction també té).
    const asSubTask = props.interaction as unknown as SubTask;

    const handleShowEditPrompt = (p: SubTaskPrompt, position: number) => {
        setSelectedPrompt(p);
        setSelectedPromptPosition(position);
        setShowEditPrompt(true);
    }

    const handleShowEditRoleAssignment = (r: SubTaskRole, position: number) => {
        setSelectedRole(r);
        setSelectedRolePosition(position);
        setShowEditRoleAssignment(true);
    }

    const handleHideEditPrompt = () => setShowEditPrompt(false);
    const handleHideEditRoleAssignment = () => setShowEditRoleAssignment(false);

    // ── Groups ──
    const handleAddNewGroup = () => {
        const newInteraction = Object.assign({}, props.interaction);
        const newGroup: SubTaskGroup = {
            _id: '', name: t("Group") + ' ' + (props.interaction.groups.length + 1), color: '', students: []
        };
        newInteraction.groups = [...props.interaction.groups, newGroup];
        props.saveCallback(newInteraction);
    }

    const handleDeleteGroup = (group: SubTaskGroup, position: number) => {
        // Per índex, no per _id: els grups nous comparteixen _id buit.
        const newInteraction = Object.assign({}, props.interaction);
        newInteraction.groups = props.interaction.groups.filter((_: SubTaskGroup, index) => index != position);
        props.saveCallback(newInteraction);
    }

    const handleCloneGroup = (group: SubTaskGroup) => {
        const newInteraction = Object.assign({}, props.interaction);
        newInteraction.groups = [...props.interaction.groups, group];
        props.saveCallback(newInteraction);
    }

    const saveGroup = (group: SubTaskGroup, name: string, color: string, students: SubTaskStudent[], position: number) => {
        const newInteraction = Object.assign({}, props.interaction);
        newInteraction.groups = props.interaction.groups.map((g: SubTaskGroup, index) => {
            if (index == position) {
                const updatedGroup = Object.assign({}, g);
                updatedGroup.name = name;
                updatedGroup.color = color;
                updatedGroup.students = students;
                return updatedGroup;
            }
            return g;
        });
        // Recalcula a quins grups pertany cada rol.
        newInteraction.roles = newInteraction.roles.map((role: SubTaskRole) => {
            const updatedRole = Object.assign({}, role);
            const groupsForThisRole: number[] = [];
            newInteraction.groups.forEach((g: SubTaskGroup, groupIndex: number) => {
                const used = g.students.some((s: SubTaskStudent) => s.role === newInteraction.roles.indexOf(role));
                if (used && !groupsForThisRole.includes(groupIndex)) groupsForThisRole.push(groupIndex);
            });
            updatedRole.groups = groupsForThisRole;
            return updatedRole;
        });
        props.saveCallback(newInteraction);
    }

    // ── Roles ──
    const handleAddNewRole = () => {
        const newInteraction = Object.assign({}, props.interaction);
        const newRole: SubTaskRole = { _id: '', name: 'R' + (props.interaction.roles.length + 1), color: '', groups: [] };
        newInteraction.roles = [...props.interaction.roles, newRole];
        props.saveCallback(newInteraction);
    }

    const handleSaveRole = (role: SubTaskRole, name: string, color: string, position: number) => {
        const newInteraction = Object.assign({}, props.interaction);
        newInteraction.roles = props.interaction.roles.map((r: SubTaskRole, index) => {
            if (index == position) {
                const updatedRole = Object.assign({}, r);
                updatedRole.name = name;
                updatedRole.color = color;
                return updatedRole;
            }
            return r;
        });
        props.saveCallback(newInteraction);
    }

    const handleDeleteRole = (role: SubTaskRole, position: number) => {
        // Per índex, no per _id: els rols nous comparteixen _id buit.
        const newInteraction = Object.assign({}, props.interaction);
        newInteraction.roles = props.interaction.roles.filter((_: SubTaskRole, index) => index != position);
        props.saveCallback(newInteraction);
    }

    const handleSaveRoleAssignment = (role: SubTaskRole, position: number) => {
        const newInteraction = Object.assign({}, props.interaction);
        newInteraction.roles = props.interaction.roles.map((r: SubTaskRole, index) => {
            if (index == position) {
                const newRole = Object.assign({}, r);
                newRole.groups = role.groups;
                return newRole;
            }
            return r;
        });
        setShowEditRoleAssignment(false);
        props.saveCallback(newInteraction);
    }

    // ── Prompts ──
    const handleAddNewPrompt = () => {
        const newInteraction = Object.assign({}, props.interaction);
        const newPrompt: SubTaskPrompt = {
            _id: '', name: t("Prompt") + ' ' + (props.interaction.prompts.length + 1),
            instructions: '', media: [], text: '', formatted_text: '', roles: [], teacher_notes: ''
        };
        newInteraction.prompts = [...props.interaction.prompts, newPrompt];
        props.saveCallback(newInteraction);
    }

    const handleSavePrompt = (prompt: SubTaskPrompt, position: number) => {
        const newInteraction = Object.assign({}, props.interaction);
        newInteraction.prompts = props.interaction.prompts.map((p: SubTaskPrompt, index) => {
            if (index == position) {
                const newPrompt = Object.assign({}, p);
                newPrompt.instructions = prompt.instructions;
                newPrompt.media = prompt.media.map((m: Media) => m);
                newPrompt.name = prompt.name;
                newPrompt.text = prompt.text;
                newPrompt.formatted_text = prompt.formatted_text;
                newPrompt.teacher_notes = prompt.teacher_notes;
                newPrompt.roles = prompt.roles;
                return newPrompt;
            }
            return p;
        });
        setShowEditPrompt(false);
        props.saveCallback(newInteraction);
    }

    const handleDeletePrompt = (prompt: SubTaskPrompt, position: number) => {
        const newInteraction = Object.assign({}, props.interaction);
        // Neteja l'assignació de prompt als estudiants que el tenien.
        newInteraction.groups = props.interaction.groups.map((g: SubTaskGroup) => {
            const newGroup = Object.assign({}, g);
            newGroup.students = g.students.map((s: SubTaskStudent) => {
                const newStudent = Object.assign({}, s);
                if (newStudent.prompt == position) newStudent.prompt = -1;
                return newStudent;
            });
            return newGroup;
        });
        newInteraction.prompts = props.interaction.prompts.filter((p: SubTaskPrompt, index) => index != position);
        props.saveCallback(newInteraction);
    }

    const handleClonePrompt = (p: SubTaskPrompt) => {
        const newInteraction = Object.assign({}, props.interaction);
        const newPrompt: SubTaskPrompt = {
            _id: '', name: p.name + ' ' + t("(copy)"),
            instructions: p.instructions, media: p.media.map((m: Media) => m),
            text: p.text, formatted_text: p.formatted_text, roles: p.roles, teacher_notes: p.teacher_notes
        };
        newInteraction.prompts = [...props.interaction.prompts, newPrompt];
        props.saveCallback(newInteraction);
    }

    const handleClose = () => props.closeCallback();
    const nullSubmit = (e: any) => e.preventDefault();

    // Manté la capçalera del modal pare sincronitzada amb la sub-vista activa.
    useEffect(() => {
        if (!props.embedded || !props.onTitle) return;
        if (showEditPrompt) props.onTitle(t("Edit Prompt"));
        else if (showEditRoleAssignment) props.onTitle(t("Edit Role Assignment"));
        else props.onTitle(null);
    }, [showEditPrompt, showEditRoleAssignment, props.embedded]);

    const designerBody = (
                    <div className="inner">
                        <Form className="edit-sub-task-form" onSubmit={ nullSubmit }>
                            <div className="sub-task-components">
                                <div className="left">
                                    <div className="groups">
                                        { props.interaction.groups.map((g: SubTaskGroup, index) => (
                                            <SubTaskGroupItem group={ g } key={ 'group-' + index } position={ index }
                                                saveCallback={ saveGroup } deleteCallback={ handleDeleteGroup } cloneCallback={ handleCloneGroup }
                                                roles={ props.interaction.roles } prompts={ props.interaction.prompts } />
                                        )) }
                                        <div className="add-group">
                                            <a title={ t("Add new group") } onClick={ handleAddNewGroup }>{ t("Add new group") }</a>
                                        </div>
                                    </div>
                                </div>
                                <div className="right">
                                    <div className="roles">
                                        <h4>{ t("Roles") }<Help id={ '650b0d67b0f9abea0305888f' } /></h4>
                                        <a className="add-new" title={ t("Add new role") } onClick={ handleAddNewRole }>{ t("Add new role") }</a>
                                        <div className="roles-list">
                                            { props.interaction.roles.map((r: SubTaskRole, index) => (
                                                <SubTaskRoleItem role={ r } key={ 'role-' + index } position={ index }
                                                    editCallback={ handleShowEditRoleAssignment }
                                                    saveCallback={ handleSaveRole }
                                                    deleteCallback={ handleDeleteRole }
                                                    assignmentsCallback={ handleShowEditRoleAssignment } />
                                            )) }
                                        </div>
                                    </div>
                                    <div className="prompts">
                                        <h4>{ t("Prompts") }<Help id={ '650b1253b0f9abea030588b6' } /></h4>
                                        <a className="add-new" title={ t("Add new prompt") } onClick={ handleAddNewPrompt }>{ t("Add new prompt") }</a>
                                        <div className="prompts-list">
                                            { props.interaction.prompts.map((p: SubTaskPrompt, index) => (
                                                <SubTaskPromptItem prompt={ p } key={ 'prompt-' + index } position={ index }
                                                    editPrompt={ handleShowEditPrompt }
                                                    deletePrompt={ handleDeletePrompt }
                                                    clonePrompt={ handleClonePrompt } />
                                            )) }
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="actions">
                                <button className="btn btn-primary" type="button" onClick={ handleClose }>{ t("Done") }</button>
                            </div>
                        </Form>
                    </div>
    );

    // Mode embedded: una sola vista a la vegada dins del modal del pare (sense
    // Overlays apilats). En sub-editar un prompt/rol, s'oculta el dissenyador i
    // es mostra l'editor amb un botó Enrere.
    if (props.embedded) {
        if (showEditPrompt && selectedPrompt) {
            return (
                <SubTaskPromptEdit task_id={ props.taskId } task_language={ props.task_language } subTask={ asSubTask }
                    prompt={ selectedPrompt } position={ selectedPromptPosition } save={ handleSavePrompt }
                    cancelCallback={ handleHideEditPrompt } />
            );
        }
        if (showEditRoleAssignment && selectedRole) {
            return (
                <SubTaskRoleItemAssignment subTask={ asSubTask } role={ selectedRole }
                    position={ selectedRolePosition } saveCallback={ handleSaveRoleAssignment }
                    cancelCallback={ handleHideEditRoleAssignment } />
            );
        }
        return designerBody;
    }

    return (
        <>
            { !showEditPrompt &&
                <Overlay title={ props.title || t("Edit Interaction") } onClose={ handleClose } className="edit-sub-task-container interaction-activity">
                    { designerBody }
                </Overlay>
            }
            { showEditPrompt && selectedPrompt &&
                <Overlay title={ t("Edit Prompt") } onClose={ handleHideEditPrompt }>
                    <SubTaskPromptEdit task_id={ props.taskId } task_language={ props.task_language } subTask={ asSubTask }
                        prompt={ selectedPrompt } position={ selectedPromptPosition } save={ handleSavePrompt } />
                </Overlay>
            }
            { showEditRoleAssignment && selectedRole &&
                <Overlay title={ t("Edit Role Assignment") } onClose={ handleHideEditRoleAssignment }>
                    <SubTaskRoleItemAssignment subTask={ asSubTask } role={ selectedRole }
                        position={ selectedRolePosition } saveCallback={ handleSaveRoleAssignment } />
                </Overlay>
            }
        </>
    );
}
